import VenueNoteView from "@/components/VenueDetailBasic/Feature/VenueNoteView";
import EmptyMapperNotes from "@/assets/images/empty-mappernotes.svg";

type PublicViewProps = {
  allBusinessNotes: any[];
  onRefresh: () => void;
  onEdit: (note: any) => void;
  onFlagNote: (note: any) => void;
  showMapperView: boolean;
  // Props for separate action arrays
  businessNotesActionsVenueNotes?: any[];
  businessNotesActions?: any[];
  isFirstSection?: boolean;
  // Venue context for the per-card resolve-alert dialog events.
  businessId: string;
  placeName: string;
};

const PublicView = ({
  allBusinessNotes,
  onRefresh,
  onEdit,
  onFlagNote,
  businessNotesActionsVenueNotes,
  businessNotesActions,
  isFirstSection = false,
  businessId,
  placeName,
}: PublicViewProps) => {
  if (allBusinessNotes.length === 0) {
    return (
      <div className="flex-1 m-auto w-full">
        <p className="text-center title-s-semibold my-10">
          No alerts for this venue yet
        </p>
        <img
          src={EmptyMapperNotes}
          alt="No search results"
          className="max-w-[70%] mx-auto"
          aria-hidden="true"
        />
      </div>
    );
  }

  // Group notes by action_title
  const groupedNotes: Record<string, any[]> = allBusinessNotes.reduce(
    (groups, note) => {
      const actionTitle =
        note.action_title ||
        (note.note && note.note.action_title) ||
        (note.details && note.details.action_title) ||
        "Other";

      if (!groups[actionTitle]) {
        groups[actionTitle] = [];
      }
      groups[actionTitle].push(note);
      return groups;
    },
    {} as Record<string, any[]>,
  );

  return (
    <div className="mt-2 space-y-6">
      {Object.entries(groupedNotes)
        .sort(([actionTitleA], [actionTitleB]) => {
          // Find the corresponding action objects to get their IDs for sorting
          // First check in businessNotesActionsVenueNotes (public notes)
          let actionA = businessNotesActionsVenueNotes?.find(
            (action) => action.title === actionTitleA,
          );
          let actionB = businessNotesActionsVenueNotes?.find(
            (action) => action.title === actionTitleB,
          );

          // If not found in venue notes, check in businessNotesActions (mapper notes)
          if (!actionA) {
            actionA = businessNotesActions?.find(
              (action) => action.title === actionTitleA,
            );
          }
          if (!actionB) {
            actionB = businessNotesActions?.find(
              (action) => action.title === actionTitleB,
            );
          }

          if (actionA && actionB) {
            // Sort by display_order in ascending order
            if (actionA.display_order !== actionB.display_order) {
              return actionA.display_order - actionB.display_order;
            }
          }

          // If IDs are the same or actions not found, maintain original order
          return 0;
        })
        .map(([actionTitle, notes], index) => (
          <div
            key={actionTitle}
            className={
              isFirstSection && index === 0 ? "space-y-3" : "space-y-3 mt-10"
            }
          >
            {/* Action Title Header */}
            <div className="border-b border-gray-200 py-2">
              <h2 className="label-l-bold text-fg-neutral-strong">
                {actionTitle} ({notes.length})
              </h2>
            </div>

            {/* Notes under this action title */}
            <div className="space-y-4">
              {notes.map((note, index) => (
                <VenueNoteView
                  key={`${actionTitle}-${index}`}
                  note={note}
                  onRefresh={onRefresh}
                  onEdit={() => onEdit(note)}
                  onFlagNote={() => onFlagNote(note)}
                  businessId={businessId}
                  placeName={placeName}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export default PublicView;
